import { API_BASE } from "./config";

/** What the person on the preview page did, batched and sent to our own server.
 *
 *  First party only: no cookies, no local storage of any identity, nothing
 *  shared with anyone. The token in the URL is already who they are, so nothing
 *  device-shaped is collected on top of it. There is no fingerprinting surface
 *  here for the same reason the photos are deleted after 30 days.
 *
 *  The page does keep one list in the browser, "minime.previews": the links of
 *  the previews this browser has opened, so it can show their other previews
 *  (preview/otherPreviews.ts). It goes only to /api/others, and never here.
 *
 *  A dropped event costs a statistic, never the page, so nothing is retried and
 *  nothing here can throw into a render. */

type Payload = Record<string, string | number | boolean>;
interface Queued {
  t: string;
  at: number;
  d?: Payload;
}

let token = "";
let queue: Queued[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let startedAt = Date.now();
let maxScroll = 0;
const once = new Set<string>();

export function startTracking(pageToken: string): () => void {
  token = pageToken;
  startedAt = Date.now();
  queue = [];
  once.clear();

  const onScroll = () => {
    const height = document.body.scrollHeight - window.innerHeight;
    if (height > 0) {
      maxScroll = Math.max(maxScroll, Math.round((window.scrollY / height) * 100));
    }
  };
  const onHide = () => {
    if (document.visibilityState === "hidden") {
      track("page.left", { ms: Date.now() - startedAt, scroll: maxScroll });
      flush();
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onHide);

  return () => {
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onHide);
    flush();
  };
}

export function track(name: string, data?: Payload): void {
  if (!token) return;
  // `at` is milliseconds since the page opened, not a wall clock: the server
  // stamps the real time, and a client clock is neither trustworthy nor
  // anybody's business.
  queue.push({ t: name, at: Date.now() - startedAt, ...(data ? { d: data } : {}) });
  if (queue.length >= 8) flush();
  else schedule();
}

/** For the things that should be counted once however many times they happen:
 *  a video that loops, a section scrolled past twice. */
export function trackOnce(name: string, data?: Payload): void {
  const key = name + JSON.stringify(data ?? {});
  if (once.has(key)) return;
  once.add(key);
  track(name, data);
}

function schedule(): void {
  if (timer) return;
  timer = setTimeout(flush, 2000);
}

export function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!token || queue.length === 0) return;

  const body = JSON.stringify({ token, events: queue.splice(0, 20) });
  const url = `${API_BASE}/api/track`;

  // text/plain keeps the beacon a simple request, so it never pays for a
  // preflight on the sort of connection this page is opened on.
  const type = "text/plain;charset=UTF-8";
  try {
    if (navigator.sendBeacon?.(url, new Blob([body], { type }))) return;
  } catch {
    /* falls through to fetch */
  }
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": type },
    body,
    keepalive: true,
  }).catch(() => undefined);
}
